import React, { useEffect, useState, useRef } from 'react';
import VanillaTilt from '../../utils/vanilla-tilt';
import comboMultiplierImg from '../../assets/combo_multiplier_card.png';

/**
 * Combo Achieved Multiplier Modal - Smooth 3D Tilt Edition
 * Displays the exact high-fidelity 1.5X Combo Multiplier artwork,
 * powered by VanillaTilt for silky-smooth 3D parallax tilt, spring easing,
 * and mobile touch/gyroscope responsiveness.
 */
export function MultiplierComboModal({
  onClose,
  multiplier = '1.5X',
  autoCloseMs = 6000,
}) {
  const [visible, setVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const tiltRef = useRef(null);

  useEffect(() => {
    const timerIn = setTimeout(() => setVisible(true), 30);
    let timerOut;
    if (autoCloseMs) {
      timerOut = setTimeout(() => {
        handleDismiss();
      }, autoCloseMs);
    }
    return () => {
      clearTimeout(timerIn);
      if (timerOut) clearTimeout(timerOut);
    };
  }, [autoCloseMs]);

  // Initialize VanillaTilt for buttery-smooth 3D physics tilt
  useEffect(() => {
    const node = tiltRef.current;
    if (node) {
      VanillaTilt.init(node, {
        max: 16,               // Max tilt rotation in degrees
        perspective: 1000,     // 3D perspective distance
        scale: 1.02,           // Subtle 3D lift on interaction
        speed: 900,            // Speed of enter/exit transition
        easing: 'cubic-bezier(.03,.98,.52,.99)', // Smooth spring easing
        gyroscope: true,       // Tilt with mobile device orientation
        gyroscopeMinAngleX: -45,
        gyroscopeMaxAngleX: 45,
        gyroscopeMinAngleY: -45,
        gyroscopeMaxAngleY: 45,
        reset: true,           // Springs back to (0, 0) on release
      });
    }

    return () => {
      if (node && node.vanillaTilt) {
        node.vanillaTilt.destroy();
      }
    };
  }, []);

  const handleDismiss = () => {
    if (isClosing) return;
    setIsClosing(true);
    setVisible(false);
    setTimeout(() => {
      if (onClose) onClose();
    }, 300);
  };

  // Touch event bridge to VanillaTilt for silky-smooth mobile touch drag
  const handleTouchStart = (e) => {
    if (e.target.closest('button')) return;
    const node = tiltRef.current;
    if (node && node.vanillaTilt && e.touches[0]) {
      node.vanillaTilt.onMouseEnter();
      node.vanillaTilt.onMouseMove({
        clientX: e.touches[0].clientX,
        clientY: e.touches[0].clientY,
      });
    }
  };

  const handleTouchMove = (e) => {
    const node = tiltRef.current;
    if (node && node.vanillaTilt && e.touches[0]) {
      node.vanillaTilt.onMouseMove({
        clientX: e.touches[0].clientX,
        clientY: e.touches[0].clientY,
      });
    }
  };

  const handleTouchEnd = () => {
    const node = tiltRef.current;
    if (node && node.vanillaTilt) {
      node.vanillaTilt.onMouseLeave();
    }
  };

  return (
    <div
      onClick={handleDismiss}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 99999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0, 0, 0, 0.65)',
        transition: 'opacity 0.28s ease',
        opacity: visible ? 1 : 0,
        cursor: 'pointer',
        overflow: 'hidden',
        perspective: '1200px',
        WebkitPerspective: '1200px',
        touchAction: 'none',
      }}
    >
      <style>{`
        @keyframes comboModalEntrance {
          0% {
            transform: scale(0.65) translateY(45px);
            opacity: 0;
            filter: brightness(1.7) drop-shadow(0 0 40px rgba(16, 185, 129, 0.6));
          }
          65% {
            transform: scale(1.04) translateY(-5px);
            opacity: 1;
            filter: brightness(1.1) drop-shadow(0 0 30px rgba(245, 158, 11, 0.5));
          }
          100% {
            transform: scale(1) translateY(0);
            opacity: 1;
            filter: brightness(1) drop-shadow(0 0 20px rgba(16, 185, 129, 0.4));
          }
        }

        @keyframes cardHeartbeatPulse {
          0%, 100% {
            transform: scale(1) translateY(0);
            filter: drop-shadow(0 15px 30px rgba(0, 0, 0, 0.55)) drop-shadow(0 0 18px rgba(16, 185, 129, 0.45));
          }
          50% {
            transform: scale(1.026) translateY(-2px);
            filter: drop-shadow(0 18px 36px rgba(0, 0, 0, 0.65)) drop-shadow(0 0 30px rgba(16, 185, 129, 0.7));
          }
        }

        @keyframes fullScreenGlowPulse {
          0%, 100% {
            opacity: 0.45;
            transform: scale(1);
          }
          50% {
            opacity: 0.75;
            transform: scale(1.03);
          }
        }

        @keyframes nextBtnShine {
          0% {
            box-shadow: 0 0 15px rgba(52, 211, 153, 0.4), inset 0 0 10px rgba(255, 255, 255, 0.2);
          }
          50% {
            box-shadow: 0 0 30px rgba(52, 211, 153, 0.85), inset 0 0 20px rgba(255, 255, 255, 0.45);
          }
          100% {
            box-shadow: 0 0 15px rgba(52, 211, 153, 0.4), inset 0 0 10px rgba(255, 255, 255, 0.2);
          }
        }

        .combo-btn-next {
          transition: transform 0.15s ease, box-shadow 0.2s ease, background-color 0.2s ease;
        }
        .combo-btn-next:hover {
          transform: scale(1.02);
          background: rgba(52, 211, 153, 0.14) !important;
          animation: nextBtnShine 1.5s infinite;
        }
        .combo-btn-next:active {
          transform: scale(0.97) !important;
        }

        .combo-btn-close-subtle {
          transition: transform 0.15s ease, background-color 0.2s ease;
        }
        .combo-btn-close-subtle:hover {
          transform: scale(1.15);
          background: rgba(255, 255, 255, 0.15) !important;
        }
      `}</style>

      {/* Light Full-Screen Ambient Radiance Spread */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          background: 'radial-gradient(circle at center, rgba(16, 185, 129, 0.18) 0%, rgba(16, 185, 129, 0.06) 45%, transparent 75%)',
          animation: 'fullScreenGlowPulse 6.0s ease-in-out infinite',
          pointerEvents: 'none',
        }}
      />

      {/* Entrance Animation Wrapper (Isolated from VanillaTilt) */}
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'relative',
          width: 'min(92vw, 440px)',
          maxWidth: '440px',
          animation: 'comboModalEntrance 0.38s cubic-bezier(0.16, 1, 0.3, 1) forwards',
          transformOrigin: 'center center',
          userSelect: 'none',
          cursor: 'default',
        }}
      >
        {/* BUTTERY-SMOOTH VANILLATILT CARD ELEMENT */}
        <div
          ref={tiltRef}
          style={{
            position: 'relative',
            width: '100%',
            transformStyle: 'preserve-3d',
            WebkitTransformStyle: 'preserve-3d',
            cursor: 'grab',
            touchAction: 'none',
          }}
        >
          {/* Dynamic Heartbeat Breathing Card Body (Ultra Slow 6.0s cycle) */}
          <div
            style={{
              position: 'relative',
              width: '100%',
              animation: 'cardHeartbeatPulse 6.0s ease-in-out infinite',
              transformOrigin: 'center center',
            }}
          >
            {/* Exact Artwork Image */}
            <img
              src={comboMultiplierImg}
              alt="Combo Achieved 1.5X Multiplier"
              style={{
                width: '100%',
                height: 'auto',
                display: 'block',
                pointerEvents: 'none',
              }}
            />

            {/* Subtle Close (X) Hitbox in Top-Right Corner */}
            <button
              className="combo-btn-close-subtle"
              onClick={handleDismiss}
              title="Close"
              style={{
                position: 'absolute',
                top: '7.5%',
                right: '8.5%',
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: 0,
                outline: 'none',
                zIndex: 10,
              }}
              aria-label="Close"
            />

            {/* Interactive "NEXT ->" Button Hitbox */}
            <button
              className="combo-btn-next"
              onClick={handleDismiss}
              title="Continue to Next Prediction"
              style={{
                position: 'absolute',
                left: '14.5%',
                top: '77.5%',
                width: '71.0%',
                height: '12.5%',
                borderRadius: '35px',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: 0,
                outline: 'none',
                zIndex: 10,
              }}
              aria-label="Next Prediction"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
